import React from "react";
import { Message } from "components/Atoms/Message";
import { Button } from "semantic-ui-react";
import { type FileTypes } from "shared/utils/fileutils";
import { NoDataAvailableDiv } from "../NoDataAvailableDiv";
import { Loading } from "../panels";
import { validateExtension, validateFileLength } from "utilities/fileUploadUtils";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

interface Props {
  relatedFiles: server.dto.RelatedFiles
  selectedFiles: server.dto.File[]
  onSelectedFilesChanged: (files: server.dto.File[]) => void
  onFilesSelected: (files: server.dto.File[]) => void

  error?: string

  maxFileSize?: number
  validFileTypes: FileTypes
}

const RelatedFiles = (props: React.PropsWithChildren<Props>) => {
  const { relatedFiles, selectedFiles } = props;

  if (relatedFiles == null) {
    return <div className="grow1 flex justify-center align-center">
      <Loading />
    </div>;
  }

  const selectedFilesSet = new Set(selectedFiles.map(f => f.id));

  let validationError: string;
  for (const file of selectedFiles) {
    const extensionValid = validateExtension([file], props.validFileTypes);
    if (extensionValid !== true) {
      validationError = extensionValid;
      break;
    }

    if (props.maxFileSize != null) {
      const fileLengthValid = validateFileLength(file.contentLength, props.maxFileSize);
      if (fileLengthValid !== true) {
        validationError = fileLengthValid;
        break;
      }
    }
  }

  const error = props.error ?? validationError;
  const hasError = error != null;
  const cantSave = validationError != null;

  const onFileToggled = (file: server.dto.File) => {
    if (selectedFilesSet.has(file.id)) {
      props.onSelectedFilesChanged(selectedFiles.filter(f => f.id !== file.id));
    } else {
      props.onSelectedFilesChanged([...selectedFiles, file]);
    }
  };

  const noItems = relatedFiles.directItems.length === 0 && relatedFiles.relatedItems.length === 0;

  return <>
    <div className="grow1 scrollbox-auto flex-column gap-larger">
      <div className="flex-column gap-small">
        {relatedFiles.directItems.map(item => (
          <div key={item.id}>
            <Checkbox
              checked={selectedFilesSet.has(item.id)}
              onChange={() => { onFileToggled(item); }}
              label={`${item.fullName} (${item.contentLengthText})`}
            />
          </div>
        ))}
      </div>

      {relatedFiles.relatedItems.map((relatedItem, idx) => <div key={relatedItem.title + idx} className="flex-column gap-small">
        <div>
          {relatedItem.title}
        </div>
        <div style={{ marginLeft: 5 }} className="flex-column gap-small" >
          {relatedItem.items.map(item => (
            <div key={item.id}>
              <Checkbox
                checked={selectedFilesSet.has(item.id)}
                onChange={() => { onFileToggled(item); }}
                label={`${item.fullName} (${item.contentLengthText})`}
              />
            </div>
          ))}
        </div>
      </div>)}

      {noItems && <NoDataAvailableDiv />}
    </div>

    {hasError && <Message type="error" className="margin-top" style={{ minHeight: "3em" }}>
      {error}
    </Message>}

    {props.children}

    <div className="flex">
      <div className="grow1"></div>
      <Button disabled={cantSave} primary onClick={() => { props.onFilesSelected(selectedFiles); }}>{RESX.Buttons.BtnOk}</Button>
    </div>
  </>;
};

export default RelatedFiles;
